/* eslint-disable local-rules/no-deprecated-backpack-components */
import {
  BasicModal,
  Box,
  Button,
  Radio,
  Typography,
} from "@outschool/backpack";
import {
  ClassPost_ClassPostFragment,
  ClassPost_MentionNamesFragment,
  MessageContentInput,
  TextMimeType,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import {
  EditableMessageContent,
  EditableMessageContentSubmitType,
  useAddAttachmentMutation,
} from "@outschool/ui-components-classroom";
import { ErrorMessage, TrackedButton } from "@outschool/ui-components-shared";
import {
  ModalActionButtons,
  Select,
  Space,
} from "@outschool/ui-legacy-component-library";
import { YOUTUBE_BASE_REGEX, YoutubeParsers } from "@outschool/ui-youtube";
import range from "lodash/range";
import React, { useCallback, useMemo, useState } from "react";

import * as Env from "../../../shared/Env";
import * as User from "../../../shared/User";
import { DateTimeInput } from "../EditableDateTime";
import { CopyClassPostModal } from "./CopyClassPostModal";

type MessageContent = Partial<ClassPost_ClassPostFragment["messageContent"]>;

// We use this object as both MessageContentInput and MessageContent
type MixedMessageContent = EditableMessageContentSubmitType & {
  text: string;
  textMimeType: TextMimeType;
  originalMimeType: TextMimeType;
  htmlText: string;
};

function expandMessageContent(mc: MessageContent): MixedMessageContent {
  return {
    ...mc,
    text:
      (mc.originalMimeType === TextMimeType.TextMarkdown
        ? mc.htmlText
        : mc.text) ?? "",
    originalMimeType: mc.originalMimeType ?? TextMimeType.TextPlainWithMentions,
    textMimeType:
      mc.originalMimeType === TextMimeType.TextMarkdown
        ? TextMimeType.TextHtml
        : mc.originalMimeType ?? TextMimeType.TextPlainWithMentions,
    htmlText: mc.htmlText ?? "",
  };
}

function expandMessageContentInput(
  mci: MessageContentInput,
): MixedMessageContent {
  return {
    ...mci,
    text: mci.text ?? "",
    textMimeType: mci.textMimeType ?? TextMimeType.TextPlainWithMentions,
    originalMimeType:
      mci.textMimeType === TextMimeType.TextHtml
        ? TextMimeType.TextMarkdown
        : mci.textMimeType ?? TextMimeType.TextPlainWithMentions,
    htmlText: mci.text ?? "",
  };
}

export default function EditClassPost({
  formTitle,
  buttonLabel,
  primaryLabel,
  inline,
  messageContent = {},
  publishAt,
  section,
  activity,
  mentionNames,
  onPost = async () => {},
  hasCopyExistingPostButton,
}: {
  formTitle?: string;
  buttonLabel?: string;
  primaryLabel?: string;
  inline?: boolean;
  messageContent?: MessageContent;
  publishAt?: Date;
  section?: any;
  activity?: any;
  mentionNames?: ClassPost_MentionNamesFragment[];
  onPost?: (
    text: EditableMessageContentSubmitType["text"],
    textMimeType: EditableMessageContentSubmitType["textMimeType"],
    attachments: EditableMessageContentSubmitType["attachments"],
    video: EditableMessageContentSubmitType["video"],
    publishAt: Date,
  ) => Promise<void>;
  hasCopyExistingPostButton?: boolean;
}) {
  const { t } = useTranslation("client\\components\\classroom\\EditClassPost");
  const [open, setOpen] = useState(false);
  const [newMessageContent, setNewMessageContent] =
    useState<MixedMessageContent | null>(expandMessageContent(messageContent));

  const [openPublishAtPicker, setOpenPublishAtPicker] = useState(false);

  const handleBackToEdit = useCallback(() => {
    setOpen(!inline);
    setOpenPublishAtPicker(false);
  }, [inline]);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setOpenPublishAtPicker(false);
  }, []);

  const handlePublishClick = useCallback(
    async (publishAt: Date, message: EditableMessageContentSubmitType) => {
      const { text, textMimeType, attachments, video } =
        message || newMessageContent;

      await onPost(
        text,
        textMimeType ?? TextMimeType.TextPlainWithMentions,
        attachments,
        video,
        publishAt,
      );
      handleOnClose();
      // Clear new post field for a possible next post
      setNewMessageContent(null);
    },
    [newMessageContent, onPost, handleOnClose],
  );

  const handleSendClick = useCallback(
    async (message: EditableMessageContentSubmitType) => {
      try {
        setNewMessageContent(expandMessageContentInput(message));
        if (!publishAt || dayjs(publishAt).isAfter()) {
          setOpenPublishAtPicker(true);
        } else {
          await handlePublishClick(publishAt, message);
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    [publishAt, handlePublishClick],
  );

  if (inline) {
    return (
      <>
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          <Typography variant="h3">{formTitle}</Typography>
          {hasCopyExistingPostButton && (
            <CopyFromExistingPostButton
              section={section}
              activity={activity}
              setNewMessageContent={setNewMessageContent}
            />
          )}
        </Box>
        <EditableMessageContent
          allowRichText
          expandedRows={10}
          placeholder={t("Type your message here")}
          onSubmit={handleSendClick}
          messageContent={newMessageContent}
          classMentionsData={mentionNames}
          inClassContext={true}
          renderButton={({ submit, disabled }) => (
            <Button
              variant="contained"
              disabled={disabled}
              onClick={() => {
                submit();
              }}
              sx={(theme: $TSFixMe) => ({
                width: "100%",

                [theme.breakpoints.up("md")]: {
                  width: "auto",
                },
              })}
            >
              {buttonLabel}
            </Button>
          )}
        />

        <BasicModal
          open={openPublishAtPicker && !!newMessageContent}
          onClose={handleOnClose}
          hasCloseButton
          closeButtonArialLabel={t("close")}
        >
          <PublishAtPickerInput
            messageContent={newMessageContent}
            value={publishAt}
            onSubmit={handlePublishClick}
            onCancel={handleBackToEdit}
          />
        </BasicModal>
      </>
    );
  }

  return (
    <>
      <Button
        onClick={() => {
          setOpen(true);
          // Populate the message content with the current message content
          setNewMessageContent(expandMessageContent(messageContent));
        }}
      >
        {buttonLabel}
      </Button>
      <BasicModal
        open={openPublishAtPicker && !!newMessageContent}
        onClose={handleOnClose}
        hasCloseButton
        closeButtonArialLabel={t("close")}
      >
        <PublishAtPickerInput
          messageContent={newMessageContent}
          value={publishAt}
          onSubmit={handlePublishClick}
          onCancel={handleBackToEdit}
        />
      </BasicModal>
      <BasicModal
        open={open && !openPublishAtPicker}
        onClose={handleOnClose}
        hasCloseButton
        closeButtonArialLabel={t("close")}
      >
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          <Typography variant="h2">{formTitle}</Typography>
          {hasCopyExistingPostButton && (
            <CopyFromExistingPostButton
              section={section}
              activity={activity}
              setNewMessageContent={setNewMessageContent}
            />
          )}
        </Box>
        <EditableMessageContent
          allowRichText
          expandedRows={10}
          placeholder={t("Type your message here")}
          onSubmit={handleSendClick}
          messageContent={newMessageContent}
          inClassContext={true}
          renderButton={({ submit }) => (
            <ModalActionButtons
              primaryLabel={primaryLabel}
              onPrimary={submit}
              onCancel={() => {
                handleOnClose();
              }}
            />
          )}
        />
      </BasicModal>
    </>
  );
}

interface CopyFromExistingPostButtonProps {
  // Use of any here is unfortunate, but it's the same as the original component
  section: any;
  activity: any;
  setNewMessageContent: (message: MixedMessageContent) => void;
}

const CopyFromExistingPostButton = ({
  section,
  activity,
  setNewMessageContent,
}: CopyFromExistingPostButtonProps) => {
  const { t } = useTranslation("client\\components\\classroom\\EditClassPost");
  const [openReuseClassPostModal, setOpenReuseClassPostModal] = useState(false);

  const { addAttachment } = useAddAttachmentMutation();
  // Save each file as a separate attachment
  const createAttachments = useCallback(
    async (files: $TSFixMe) => {
      const attachments: MessageContent["attachments"] = [];
      for (const file of files) {
        const attachment = await addAttachment(file);
        if (attachment) {
          attachments.push(attachment);
        }
      }
      return attachments;
    },
    [addAttachment],
  );

  const createVideoAttachment = useCallback(
    async (file: $TSFixMe) => {
      return await addAttachment(file, true);
    },
    [addAttachment],
  );

  // Create a mutation for submitting a new attachment here.
  const handleClassPostCopySubmit = useCallback(
    async (message: MessageContent) => {
      message = { ...message }; // create new copy of graphql object

      // Create new copies of the message attachments.
      if (message.attachments && message.attachments.length) {
        const files = message.attachments.map(attachment => attachment.file);
        message.attachments = await createAttachments(files);
      }
      if (message.video) {
        const videoAttachment = await createVideoAttachment(message.video.file);
        message.video = videoAttachment;
      }
      setNewMessageContent(expandMessageContent(message));
      setOpenReuseClassPostModal(false);
    },
    [createAttachments, createVideoAttachment, setNewMessageContent],
  );

  return (
    <>
      <TrackedButton
        variant="link"
        trackingName={"copy_existing_post_from_inline_editable_text"}
        onClick={() => setOpenReuseClassPostModal(true)}
      >
        {t("Copy existing announcement…")}
      </TrackedButton>
      <BasicModal
        open={openReuseClassPostModal}
        onClose={() => setOpenReuseClassPostModal(false)}
        hasCloseButton
        closeButtonArialLabel={t("close")}
      >
        <CopyClassPostModal
          onCancel={() => setOpenReuseClassPostModal(false)}
          handleSubmit={handleClassPostCopySubmit}
          activity={activity}
          section={section}
        />
      </BasicModal>
    </>
  );
};

function PublishAtPickerInput({
  messageContent,
  value,
  onSubmit,
  onCancel,
}: $TSFixMe) {
  const { t } = useTranslation("client\\components\\classroom\\EditClassPost");
  const { currentUser } = useSession();
  const userTimeZone = User.timeZone(currentUser);

  const [publishNow, setPublishNow] = useState(!value);

  const startOfDay = dayjs(value).tz(userTimeZone).startOf("day").toDate();

  const [day, setDay] = useState(startOfDay);
  const [hour, setHour] = useState(
    (value
      ? dayjs.tz(value, userTimeZone).hour()
      : dayjs().tz(userTimeZone).hour() + 1) % 24,
  );

  const youtubeError = useMemo(() => {
    const { text } = messageContent;

    if (
      YOUTUBE_BASE_REGEX.test(text) &&
      !YoutubeParsers.find(parser => !!parser.matcher(text))
    ) {
      return t(
        "Your message includes a Youtube link which we are not able to embed. Supported URL formats are /watch, /channel and /user.",
      );
    } else {
      return;
    }
  }, [messageContent, t]);

  const publishAtError = useMemo(() => {
    const publishAt = dayjs.tz(day, userTimeZone).hour(hour).startOf("hour");
    const publishAtIsValid =
      !publishAt || (publishAt.isValid() && publishAt.isSameOrAfter(dayjs()));

    if (!publishAtIsValid && !publishNow) {
      return t("Please enter a time in the future.");
    } else {
      return;
    }
  }, [day, hour, userTimeZone, publishNow, t]);

  const publishAt = dayjs.tz(day, userTimeZone).hour(hour).startOf("hour");

  const handleSubmit = useCallback(() => {
    if (publishNow) {
      onSubmit();
    } else {
      onSubmit(publishAt);
    }
  }, [onSubmit, publishAt, publishNow]);

  const hourOptions = range(0, 24).map(hour => {
    const isPm = hour >= 12;
    return {
      value: hour,
      label: `${(isPm ? hour - 12 : hour) || 12}:00 ${isPm ? "PM" : "AM"}`,
    };
  });

  return (
    <Box>
      <Typography
        variant="h2"
        sx={{
          marginBottom: "1em",
        }}
      >
        {t("Select Publish Time")}
      </Typography>
      {!!publishAtError && (
        <ErrorMessage
          value={publishAtError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        ></ErrorMessage>
      )}
      {!!youtubeError && (
        <ErrorMessage
          value={youtubeError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        ></ErrorMessage>
      )}
      <form>
        <label>
          <Radio
            // eslint-disable-next-line i18next/no-literal-string
            value="publish-now"
            checked={publishNow}
            onChange={() => setPublishNow(true)}
          />
          <Typography
            sx={{
              display: "inline",
              marginLeft: "0.5em",
            }}
            variant="h4"
          >
            {t("Publish Now")}
          </Typography>
          <Box
            sx={{
              marginLeft: "2em",
            }}
          >
            {t(
              'The post will be visible in the Classroom and notifications will be sent when you press "Publish Now".',
            )}
          </Box>
        </label>
        <Space y="large" />
        <label>
          <Radio
            // eslint-disable-next-line i18next/no-literal-string
            value="publish-later"
            checked={!publishNow}
            onChange={() => setPublishNow(false)}
          />
          <Typography
            sx={{
              display: "inline",
              marginLeft: "0.5em",
            }}
            variant="h4"
          >
            {t("Publish Later")}
          </Typography>
          <Box
            sx={{
              marginLeft: "2em",
              marginTop: "0.5em",
            }}
          >
            <DateTimeInput
              dateTime={publishAt}
              timeZone={userTimeZone}
              onChange={publishDay => {
                setDay(
                  // eslint-disable-next-line i18next/no-literal-string
                  dayjs.tz(publishDay, userTimeZone).startOf("day").toDate(),
                );
                setPublishNow(false);
              }}
              min={startOfDay}
            />
            &nbsp;
            <Select
              value={hour}
              onChange={(newHour: number) => setHour(newHour)}
              options={hourOptions}
              disabled={publishNow}
            />
          </Box>
          <Box
            sx={{
              marginLeft: "2em",
              marginTop: "0.5em",
            }}
          >
            {t(
              "The post will become visible in the Classroom and notifications will be sent at the chosen time.",
            )}
          </Box>
        </label>
      </form>
      <Space y="large" />
      <ModalActionButtons
        primaryLabel={publishNow ? t("Publish Now") : t("Publish Later")}
        cancelLabel={t("Back to Edit")}
        onPrimary={handleSubmit}
        onCancel={onCancel}
        disabled={!!publishAtError || !!youtubeError}
      />
    </Box>
  );
}
